import { AssetManagerFea } from 'src/app/asset-manager/asset-manager-fea';
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { MenuHomeConfig } from './menu-data/menu-home-config'
import { MenuDocumentsConfig } from './menu-data/menu-documents-config'
import { AssetService } from 'src/app/shared/services/asset.service'
import { CurrentAsset } from 'src/app/shared/models/current-asset'
import { Util } from '@phdsoft/common'
import { FeaResultService } from 'src/app/fea/services/fea-result.service';
import { AssetPropertiesLoaderService } from '@app/asset-manager/asset-properties-loader.service';
import { ViewModeInspectionService } from '@app/template/view-mode-tools/view-mode-inspection/view-mode-inspection.service';
import { ViewModeStructureLabelsService } from '@app/template/view-mode-tools/view-mode-structure-labels/view-mode-structure-labels.service';
import { ViewModeElementsService } from '@app/template/view-mode-tools/view-mode-elements/view-mode-elements.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { VisualizationModeBridgeService } from '@app/shared/services/visualization-mode-bridge.service';
import { SubmodelVisualizationService } from '@app/digital-twin/submodel-visualization-window/submodel-visualization.service';
import { ViewModeLayersState, ViewModeLayersStateService } from '@app/template/view-mode-tools/view-mode-layers-state.service';
import { AssetManager } from '@app/asset-manager/asset-manager';

export type VisualizationMode =
  'DEFAULT' | 'Inspection' | 'Sensor' | 'Degradation' | 'RemainingLife' | 'CorrosionRate' | 'CorrosionRiskIndex' | 'Repair' | 'RegionColor' | 'ThicknessColor'
  | 'Span' | 'Spacing' | 'Extrude' | 'MaterialColor' | 'FEAResults' | 'PlatePanels'
  | 'StiffenedPanels' | 'LargePanels' | 'Transparent' | 'CriticalArea' | 'Risk' | 'FullIds' | 'CompletedIds' | 'FlangeElementsView' | 'StiffenersBracketsView'
  | 'ReferenceElement' | 'StructuralLabel' | 'ELEMENTS' | 'Connections' | 'Structure4D' | 'TypicalDetailsFamily'| 'LoadCycleClassification' | 'SubmodelNavigation' | string | undefined

export type AssetMode = 'CAD' | 'FEM'

@Injectable() // Do NOT provide at root. Does not make sense globally
export class MenuService {
  show: boolean
  visualizationMode: VisualizationMode;
  private visualizationModeSubject = new BehaviorSubject<VisualizationMode>(undefined);
  public visualizationMode$: Observable<VisualizationMode> = this.visualizationModeSubject.asObservable();

  constructor(
    public readonly router: Router,
    public menuHomeConfig: MenuHomeConfig,
    public menuDocumentConfig: MenuDocumentsConfig,
    private assetService: AssetService,
    private assetManagerFea: AssetManagerFea,
    private feaResultService: FeaResultService,
    private assetPropertiesLoaderService: AssetPropertiesLoaderService,
    private viewModeInspectionService: ViewModeInspectionService,
    private viewModeStructureLabelsService: ViewModeStructureLabelsService,
    private viewModeElementsService: ViewModeElementsService,
    private viewModeLayersStateService: ViewModeLayersStateService,
    private visualizationModeBridge: VisualizationModeBridgeService,
    private submodelVisualizationService: SubmodelVisualizationService,
    private assetManager: AssetManager,
  ) {
    this.visualizationModeBridge.register((mode, toggle, deactivate) =>
      this.changeVisualizationMode(mode as VisualizationMode, toggle, deactivate)
    );
  }

  async getAssetMode(assetId?: string): Promise<AssetMode> {
    let asset: CurrentAsset
    if (Util.isNullOrUndefined(assetId)) {
      asset = await this.assetService.getCurrentAsset()
    } else {
      asset = await this.assetService.getCurrentAsset(assetId)
    }
    //const asset: CurrentAsset = assetId ? await this.assetService.getCurrentAsset(assetId) : ;
    if (Util.isNullOrUndefined(asset?.AssetInfo?.IsCad)) {
      throw new Error("Asset information is incomplete or unavailable.");
    }
    return asset.AssetInfo.IsCad ? "CAD" : "FEM";
  }

  documentsMode() {
    this.menuHomeConfig.documentsMode()
  }

  async changeAssetMode(mode: AssetMode) {
    const asset: CurrentAsset = await this.assetService.getCurrentAsset();
    var assetMode = await this.getAssetMode();
    if (mode === assetMode) {
      return;
    }
    var linkedAssetId = asset.AssetInfo.LinkedAsset;
    if (Util.isNullOrUndefined(linkedAssetId)) {
      return;
    }
    await this.menuHomeConfig.navigate2LinkedAsset(linkedAssetId);
  }

  updateVisualizationModeTextOnly(mode: VisualizationMode) {
    if (this.isViewLayerMode(mode)) {
      this.applyViewLayerMode(mode, false);
      return;
    }

    this.visualizationMode = mode;
  }

  changeVisualizationMode(mode: VisualizationMode, toggle: boolean = true, deactivate: boolean = true) {
    if (this.isViewLayerMode(mode)) {
      this.applyViewLayerMode(mode, toggle);
      return;
    }

    const previousMode = this.visualizationMode;
    if (deactivate) {
      this.DeactivateVisualizationMode(previousMode, mode);
    }
    if (this.visualizationMode == mode && toggle) {
      mode = undefined;
    }
    this.resetElementsLayer();
    this.visualizationMode = mode;
    this.visualizationModeSubject.next(mode);
    this.ActivateVisualizationMode();
    this.reapplyActiveViewLayers();
  }

  undoVisualizationMode(mode: VisualizationMode) {
    if (this.isViewLayerMode(mode)) {
      this.applyViewLayerMode(mode, false);
      return;
    }

    this.resetElementsLayer();
    this.visualizationMode = mode;
    this.ActivateVisualizationMode();
    this.reapplyActiveViewLayers();
  }

  restoreViewLayers(layers: ViewModeLayersState | undefined): void {
    if (!layers) {
      return;
    }

    this.restoreElementsLayerState(layers);
    this.restoreInspectionLayerState(layers);
    this.restoreStructuralLabelsLayerState(layers);
  }

  async changeVisualizationModeAsync(mode: VisualizationMode, regionId: string, shouldWait: boolean = true) {
    if (this.isViewLayerMode(mode)) {
      this.applyViewLayerMode(mode, false);
      return;
    }

    const previousMode = this.visualizationMode;

    if (shouldWait) {
      await this.waitForAssetReady(regionId);
    }

    this.resetElementsLayer();
    this.DeactivateVisualizationMode(previousMode, mode);
    
    this.visualizationMode = mode;
    this.visualizationModeSubject.next(mode);
    this.ActivateVisualizationMode();
    this.reapplyActiveViewLayers();
  }

  IsVisualizationModeAllowedInCurrentNavigationMode(mode: VisualizationMode): boolean {
    let navigationMode = this.menuHomeConfig.getNavigationMode();
    const numberRegions = this.menuHomeConfig.getNumberOfVisibleRegions();
    const baseMode = mode?.split('|')[0];
    switch (baseMode) {
      case 'Extrude':
      case 'ReferenceElement':
      case 'StructuralLabel':
        return navigationMode == 'SECTIONS';
      case 'Inspection':
      case 'Degradation':
      case 'RemainingLife':
      case 'CorrosionRate':
      case 'CorrosionRiskIndex':
      case 'Repair':
      case 'ThicknessColor':
      case 'MaterialColor':
      case 'TypicalDetailsFamily':
      case 'Transparent':
        return (navigationMode == 'REGIONS' && numberRegions == 1) || navigationMode == 'SECTIONS';
      case 'PlatePanels':
      case 'StiffenedPanels':
      case 'LargePanels':
      case 'FEAResults':
      case 'RegionColor':
      case 'Structure4D':
      case 'Sensor':
      case 'FullIds':
      case 'CompletedIds':
      case 'FlangeElementsView':
      case 'StiffenersBracketsView':
      case undefined:
        return navigationMode == 'MODEL' || navigationMode == 'REGIONS' || navigationMode == 'SECTIONS';
      default:
        return false;
    }
  }

  private ActivateVisualizationMode() {
    const baseMode = this.visualizationMode?.split('|')[0];
    switch (baseMode) {
      case 'Risk':
        this.menuHomeConfig.riskViewMode();
        break;
      case 'Inspection':
        this.menuHomeConfig.inspectionViewMode(this.visualizationMode);
        break;
      case 'Sensor':
        this.menuHomeConfig.xRaySensor();
        break;
      case 'CriticalArea':
        this.menuHomeConfig.xRay('CriticalArea');
        break;
      case 'Degradation':
        this.menuHomeConfig.xRayDegradation();
        break;
      case 'RemainingLife':
        this.menuHomeConfig.remainingLife();
        break;
      case 'CorrosionRate':
        this.menuHomeConfig.corrosionRate();
        break;
      case 'CorrosionRiskIndex':
        this.menuHomeConfig.corrosionRiskIndex();
        break;
      case 'Repair':
        this.menuHomeConfig.xRay('Repair');
        break;
      case 'RegionColor':
        this.menuHomeConfig.regionsViewMode();
        break;
      case 'ThicknessColor':
        this.menuHomeConfig.thicknessColorViewMode();
        break;
      case 'Structure4D':
        this.menuHomeConfig.structure4DViewMode();
        break;
      case 'Span':
        break;
      case 'Spacing':
        break;
      case 'Extrude':
        // TODO: Implement extrude mode. Removed old, non working code.
        break;
      case 'MaterialColor':
        this.menuHomeConfig.materialViewMode();
        break;
      case 'FEAResults':
        // current result parameters are usually set by the command that triggers the view change
        this.assetManagerFea.enterFEA('FEAResults');
        break;
      case 'PlatePanels':
        this.feaResultService.setCurrentResultParameters("PanelRecognition", "Plate")
        this.assetManagerFea.enterFEA('PlatePanels');
        break;
      case 'StiffenedPanels':
        this.feaResultService.setCurrentResultParameters("PanelRecognition", "Stiffened")
        this.assetManagerFea.enterFEA('StiffenedPanels');
        break;
      case 'LargePanels':
        this.feaResultService.setCurrentResultParameters("PanelRecognition", "Large")
        this.assetManagerFea.enterFEA('LargePanels');
        break;
      case 'Transparent':
        this.menuHomeConfig.transparenceViewMode();
        break;
      case 'FullIds':
          this.menuHomeConfig.fullIds();
          break;
      case 'CompletedIds':
          this.menuHomeConfig.completeIds();
          break;
      case 'FlangeElementsView':
          this.menuHomeConfig.flangeElementsView();
          break;
      case 'StiffenersBracketsView':
        this.menuHomeConfig.stiffenersBracketsView();
        break;
      case 'TypicalDetailsFamily':
        this.menuHomeConfig.typicalDetailsFamilyView();
        break;
      case 'ReferenceElement':
        this.menuHomeConfig.referenceElementView();
        break;
      case 'StructuralLabel':
        this.menuHomeConfig.structuralLabelViewMode(this.visualizationMode);
        break;
      case 'ELEMENTS':
        this.menuHomeConfig.elementsViewMode(this.visualizationMode);
        break;        
      case 'SubmodelNavigation':
        this.submodelVisualizationService.activateSubmodelNavigationView();
        break;
      default:
        this.menuHomeConfig.selectDefaultViewMode();
        break;
    }
  }

  private isViewLayerMode(mode: VisualizationMode): boolean {
    const baseMode = this.getBaseMode(mode);
    return baseMode === 'Inspection' || baseMode === 'ELEMENTS' || baseMode === 'StructuralLabel';
  }

  private applyViewLayerMode(mode: VisualizationMode, toggle: boolean): void {
    const baseMode = this.getBaseMode(mode);
    const hasDetails = typeof mode === 'string' && mode.includes('|');

    switch (baseMode) {
      case 'Inspection':
        if (toggle && !hasDetails && this.viewModeLayersStateService.snapshot.inspection.active) {
          this.deactivateInspectionLayer();
          return;
        }

        if (hasDetails) {
          this.viewModeInspectionService.restoreInspectionViewMode(mode as string);
        } else {
          this.viewModeInspectionService.activateInspectionViewMode();
        }
        this.syncInspectionLayerState();
        break;

      case 'ELEMENTS':
        if (toggle && !hasDetails && this.viewModeLayersStateService.snapshot.elements.active) {
          this.deactivateElementsLayer();
          return;
        }

        if (hasDetails) {
          this.viewModeElementsService.restoreElementsViewMode(mode as string);
        } else {
          this.viewModeElementsService.activateElementsViewMode();
        }
        this.syncElementsLayerState(true);
        break;

      case 'StructuralLabel':
        if (toggle && !hasDetails && this.viewModeLayersStateService.snapshot.structuralLabels.active) {
          this.deactivateStructuralLabelsLayer();
          return;
        }

        if (hasDetails) {
          this.viewModeStructureLabelsService.restoreStructureLabelsViewMode(mode as string, this.assetManager);
        } else {
          this.viewModeStructureLabelsService.activateStructuralLabels(this.assetManager);
        }
        this.syncStructuralLabelsLayerState(true);
        break;

      default:
        break;
    }
  }

  private reapplyActiveViewLayers(): void {
    const layers = this.viewModeLayersStateService.snapshot;

    if (layers.elements.active) {
      this.viewModeElementsService.reapplyElementsViewMode();
      this.syncElementsLayerState(true);
    }

    if (layers.inspection.active) {
      this.viewModeInspectionService.reapplyInspectionViewMode();
      this.syncInspectionLayerState();
    }

    if (layers.structuralLabels.active) {
      this.viewModeStructureLabelsService.activateStructuralLabels(this.assetManager);
      if (layers.structuralLabels.selectedElementType) {
        this.viewModeStructureLabelsService.showStructuralLabels(
          this.assetManager,
          layers.structuralLabels.selectedElementType
        );
      }
      this.syncStructuralLabelsLayerState(true);
    }
  }

  private deactivateInspectionLayer(): void {
    this.viewModeInspectionService.deactivateInspectionViewMode();
    this.viewModeLayersStateService.updateInspection({ active: false });
  }

  private deactivateElementsLayer(): void {
    this.viewModeElementsService.deactivateElementsViewMode();
    this.viewModeLayersStateService.updateElements({ active: false });
  }

  private resetElementsLayer(): void {
    const isElementsLayerActive = this.viewModeLayersStateService.snapshot.elements.active || this.viewModeElementsService.isActive;
    if (isElementsLayerActive) {
      this.viewModeElementsService.deactivateElementsViewMode();
    }
    this.viewModeLayersStateService.updateElements({
      active: false,
      selectedElementTypeIds: [],
    });
  }

  private deactivateStructuralLabelsLayer(): void {
    this.viewModeStructureLabelsService.deactivateStructuralLabels(this.assetManager);
    this.viewModeLayersStateService.updateStructuralLabels({ active: false, selectedElementType: '' });
  }

  private syncInspectionLayerState(): void {
    this.viewModeLayersStateService.updateInspection({
      active: this.viewModeInspectionService.isActive,
      xray: this.viewModeInspectionService.xrayEnabled,
      anomalyLabelsHiddenByUser: this.viewModeInspectionService.anomalyLabelsHiddenByUser,
      selectedElementTypeIds: this.viewModeInspectionService.selectedElementTypeIds,
    });
  }

  private syncElementsLayerState(active: boolean): void {
    this.viewModeLayersStateService.updateElements({
      active,
      selectedElementTypeIds: this.viewModeElementsService.selectedElementTypeIds,
    });
  }

  private syncStructuralLabelsLayerState(active: boolean): void {
    this.viewModeLayersStateService.updateStructuralLabels({
      active,
      selectedElementType: this.viewModeStructureLabelsService.selectedElementType,
    });
  }

  private restoreInspectionLayerState(layers: ViewModeLayersState): void {
    const inspection = layers.inspection;
    if (!inspection?.active) {
      this.viewModeInspectionService.deactivateInspectionViewMode();
      this.viewModeLayersStateService.updateInspection({
        active: false,
        xray: false,
        anomalyLabelsHiddenByUser: false,
        selectedElementTypeIds: [],
      });
      return;
    }

    const activeOptions: string[] = [];
    if (inspection.xray) {
      activeOptions.push('xray');
    }
    if (inspection.anomalyLabelsHiddenByUser) {
      activeOptions.push('hideLabels');
    }
    const selectedElementIds = inspection.selectedElementTypeIds ?? [];
    this.viewModeInspectionService.restoreInspectionViewMode(
      `Inspection|${activeOptions.join(',')}:${selectedElementIds.join(',')}`
    );
    this.syncInspectionLayerState();
  }

  private restoreElementsLayerState(layers: ViewModeLayersState): void {
    const elements = layers.elements;
    if (!elements?.active) {
      this.viewModeElementsService.deactivateElementsViewMode();
      this.viewModeLayersStateService.updateElements({
        active: false,
        selectedElementTypeIds: [],
      });
      return;
    }

    const selectedElementIds = elements.selectedElementTypeIds ?? [];
    this.viewModeElementsService.restoreElementsViewMode(`ELEMENTS|${selectedElementIds.join(',')}`);
    this.syncElementsLayerState(true);
  }

  private restoreStructuralLabelsLayerState(layers: ViewModeLayersState): void {
    const structuralLabels = layers.structuralLabels;
    if (!structuralLabels?.active) {
      this.viewModeStructureLabelsService.deactivateStructuralLabels(this.assetManager);
      this.viewModeLayersStateService.updateStructuralLabels({
        active: false,
        selectedElementType: '',
      });
      return;
    }

    if (structuralLabels.selectedElementType) {
      this.viewModeStructureLabelsService.restoreStructureLabelsViewMode(
        `StructuralLabel|${structuralLabels.selectedElementType}`,
        this.assetManager
      );
    } else {
      this.viewModeStructureLabelsService.activateStructuralLabels(this.assetManager);
    }
    this.syncStructuralLabelsLayerState(true);
  }

  private getBaseMode(mode: VisualizationMode): string | undefined {
    return mode?.split('|')[0];
  }

  private DeactivateVisualizationMode(previousMode: VisualizationMode, nextMode: VisualizationMode) {
    const basePrevious = previousMode?.split('|')[0];
    const baseNext = nextMode?.split('|')[0];

    switch (basePrevious) {
      case 'Extrude':
        // TODO: Implement extrude mode. Removed old, non working code.
        break;
      case 'FEAResults':
      case 'PlatePanels':
      case 'StiffenedPanels':
      case 'LargePanels':
        if (baseNext !== 'ELEMENTS') {
          this.assetManagerFea.exitFEM();
        }
        break;
      case 'SubmodelNavigation':
        this.submodelVisualizationService.deactivateSubmodelNavigationView();
        break;
      default:
        break;
    }

    if (baseNext !== 'ELEMENTS') {
      this.menuHomeConfig.selectDefaultViewMode();
    }
  }

  private async waitForAssetReady(regionId: string): Promise<void> {
    if (this.assetPropertiesLoaderService.isRegionPropertiesLoaded(regionId)) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const subscription = this.assetPropertiesLoaderService.getRegionPropertiesLoaded(regionId).subscribe((loaded) => {
        if (loaded) {
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }
}
